import React from 'react';
import { Button, Collapse, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AgentDto } from '@shared/services/apiService/apiService';
import { JsonInput } from '@shared/components/JsonInput/JsonInput';
import { HandoffCanvas } from '@features/Admin/Agent/ui/HandoffCanvas';
import { HandoffRuleEditorValue } from '@features/Admin/Agent/utils/handoff-rule-mapper';
import { useTranslation } from 'react-i18next';

interface StructuredPanelProps {
  isOpen: boolean;
  rules: HandoffRuleEditorValue[];
  canvasAgentIds: string[];
  canvasAgents: AgentDto[];
  nodePositions: Record<string, { x: number; y: number }>;
  selectedRuleId: string | null;
  isDisabled: boolean;
  onOpenRule: (ruleId: string) => void;
  onConnectAgents: (sourceAgentId: string, targetAgentId: string) => void;
  onNodePositionChange: (agentId: string, position: { x: number; y: number }) => void;
  onAddAssistant: () => void;
  highlightedRuleId: string | null;
  isDirty: boolean;
  onNodeSelect: (agentId: string) => void;
  canvasHeight?: number | string;
}

export const HandoffStructuredPanel: React.FC<StructuredPanelProps> = ({
  isOpen,
  rules,
  canvasAgentIds,
  canvasAgents,
  nodePositions,
  selectedRuleId,
  isDisabled,
  onOpenRule,
  onConnectAgents,
  onNodePositionChange,
  onAddAssistant,
  highlightedRuleId,
  isDirty,
  onNodeSelect,
  canvasHeight,
}) => {
  const { t } = useTranslation();
  return (
    <Collapse in={isOpen} unmountOnExit>
      {canvasAgentIds.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            height: canvasHeight || 'calc(100vh - 56px)',
            borderRadius: 0,
            display: 'grid',
            placeItems: 'center',
            textAlign: 'center',
            p: 3,
          }}>
          <Stack spacing={1.5} alignItems="center">
            <Typography variant="h6">
              {t('campaignPage.squads.editor.empty.addAssistantToStart')}
            </Typography>
            <Tooltip
              title={
                isDisabled ? 'Assistant updates are unavailable while save is in progress.' : ''
              }>
              <span>
                <Button
                  onClick={onAddAssistant}
                  disabled={isDisabled}
                  variant="contained"
                  startIcon={<AddIcon />}>
                  {t('campaignPage.squads.editor.actions.addAssistant')}
                </Button>
              </span>
            </Tooltip>
            {isDisabled && (
              <Typography variant="caption" color="text.secondary">
                Assistant updates are disabled while save is in progress.
              </Typography>
            )}
          </Stack>
        </Paper>
      ) : (
        <HandoffCanvas
          rules={rules}
          agents={canvasAgents}
          height={canvasHeight || 'calc(100vh - 56px)'}
          nodePositions={nodePositions}
          selectedRuleId={selectedRuleId}
          highlightedRuleId={highlightedRuleId}
          hasUnsavedChanges={isDirty}
          disabled={isDisabled}
          onSelectRule={(ruleId) => onOpenRule(ruleId)}
          onNodeSelect={onNodeSelect}
          onConnectAgents={onConnectAgents}
          onNodePositionChange={onNodePositionChange}
        />
      )}
    </Collapse>
  );
};

interface JsonPanelProps {
  isOpen: boolean;
  value: string;
  jsonError: string | null;
  isDisabled: boolean;
  onChange: (value: string) => void;
  onRevertToLastValid: () => void;
  onFormatJson: () => void;
  canRecoverJson: boolean;
  jsonEditorHeight?: number | string;
}

export const HandoffJsonPanel: React.FC<JsonPanelProps> = ({
  isOpen,
  value,
  jsonError,
  isDisabled,
  onChange,
  onRevertToLastValid,
  onFormatJson,
  canRecoverJson,
  jsonEditorHeight,
}) => {
  const { t } = useTranslation();
  return (
    <Collapse in={isOpen} unmountOnExit>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="text" disabled={isDisabled} onClick={onFormatJson}>
            Format JSON
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={isDisabled || !canRecoverJson}
            onClick={onRevertToLastValid}>
            Revert to last valid
          </Button>
        </Stack>
        <JsonInput
          rows={14}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          error={jsonError}
          sx={
            jsonEditorHeight
              ? {
                  '& .MuiInputBase-root': {
                    height: jsonEditorHeight,
                    alignItems: 'stretch',
                  },
                  '& .MuiInputBase-inputMultiline': {
                    height: '100% !important',
                    overflow: 'auto !important',
                  },
                }
              : undefined
          }
          placeholder={`[
  {
    "name": "agent-1-to-agent-2",
    "fromAgentId": "agent-1-id",
    "toAgentId": "agent-2-id",
    "messages": {
      "start": ["Transferring you now..."],
      "delayed": [],
      "complete": [],
      "failed": []
    },
    "contextSummaryPlan": {
      "summary": "Customer verified account and requested refund status."
    },
    "sessionBoundaryPlan": {
      "startAgentId": "agent-1-id",
      "endingAgentIds": ["agent-2-id"],
      "allowSessionWithoutEndingAgent": true
    },
    "variableExtractionPlan": {
      "strict": true,
      "schema": {
        "type": "object",
        "additionalProperties": false,
        "required": [],
        "properties": {}
      }
    }
  }
]`}
        />
        <Typography variant="caption" color="text.secondary">
          {t('campaignPage.squads.editor.legacyJsonHint')}
        </Typography>
      </Stack>
    </Collapse>
  );
};
